import { z } from "zod";

// Mirrors the contract's name rule, which rejects control characters.
// eslint-disable-next-line no-control-regex
const CONTROL_OR_ANGLE = /[\u0000-\u001f<>]/;
const NAME_MAX = 80;
const RELATION_OTHER_TEXT_MAX = 80;
const REFERENCE_MAX = 64;
const MIN_AGE = 0;
const MAX_AGE = 120;

const emailSchema = z.string().trim().email().max(254);

export function isValidName(value: string): boolean {
  const trimmed = value.trim();
  return (
    trimmed.length >= 1 &&
    trimmed.length <= NAME_MAX &&
    !CONTROL_OR_ANGLE.test(trimmed)
  );
}

export function isValidZip(value: string): boolean {
  return /^\d{5}$/.test(value);
}

export function isValidEmail(value: string): boolean {
  return emailSchema.safeParse(value).success;
}

/** The ten national digits of a US phone number, tolerating a leading 1
 * and any punctuation. Null when the input is not a ten digit number. */
export function nationalPhoneDigits(value: string): string | null {
  const digits = value.replace(/\D/g, "");
  const national =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  return national.length === 10 ? national : null;
}

export function isValidPhone(value: string): boolean {
  return nationalPhoneDigits(value) !== null;
}

export function isValidRelationOtherText(value: string): boolean {
  const trimmed = value.trim();
  return (
    trimmed.length >= 1 &&
    trimmed.length <= RELATION_OTHER_TEXT_MAX &&
    !CONTROL_OR_ANGLE.test(trimmed)
  );
}

export function isValidReference(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.length <= REFERENCE_MAX && !CONTROL_OR_ANGLE.test(trimmed);
}

export type DobResult =
  { ok: true; iso: string } | { ok: false; message: string };

function wholeYearsBetween(
  year: number,
  month: number,
  day: number,
  today: Date,
): number {
  let years = today.getUTCFullYear() - year;
  const beforeBirthday =
    today.getUTCMonth() + 1 < month ||
    (today.getUTCMonth() + 1 === month && today.getUTCDate() < day);
  if (beforeBirthday) years -= 1;
  return years;
}

/** Reads an MM/DD/YYYY date of birth: a real calendar day, not in the
 * future, and an age of 0 to 120. `today` is injectable for tests. */
export function parseDob(text: string, today: Date = new Date()): DobResult {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text.trim());
  if (!match) return { ok: false, message: "Enter a date as MM/DD/YYYY" };
  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  const isRealDay =
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day;
  if (!isRealDay) return { ok: false, message: "That date doesn't exist" };
  const age = wholeYearsBetween(year, month, day, today);
  if (date.getTime() > today.getTime()) {
    return { ok: false, message: "That date is in the future" };
  }
  if (age < MIN_AGE || age > MAX_AGE) {
    return { ok: false, message: "Please check the year" };
  }
  return {
    ok: true,
    iso: `${match[3]}-${match[1]}-${match[2]}`,
  };
}

export function isValidDob(text: string, today?: Date): boolean {
  return parseDob(text, today).ok;
}

export function nameError(value: string, label: string): string | null {
  if (value.trim() === "") return `${label} is required`;
  return isValidName(value) ? null : `Please check the ${label.toLowerCase()}`;
}

export function zipError(value: string): string | null {
  return isValidZip(value) ? null : "Enter a 5-digit ZIP code";
}

export function emailError(value: string): string | null {
  if (value.trim() === "") return "Email is required";
  return isValidEmail(value) ? null : "That doesn't look like an email address";
}

/** What the visitor reads when the server refuses a phone number as not a
 * real one. */
export const PHONE_REJECTED_MESSAGE =
  "That phone number doesn’t look real. Please check it.";

export function phoneError(value: string): string | null {
  if (value.replace(/\D/g, "") === "") return "Phone number is required";
  return isValidPhone(value) ? null : "Enter all 10 digits of the phone number";
}

export function dobError(text: string): string | null {
  const parsed = parseDob(text);
  return parsed.ok ? null : parsed.message;
}

export function relationOtherTextError(value: string): string | null {
  if (value.trim() === "") return "Please tell us a little more";
  return isValidRelationOtherText(value)
    ? null
    : `Please keep this to ${RELATION_OTHER_TEXT_MAX} characters`;
}
